/**
 * WF / Objekty k připojení — agregace workflow poznámek z budovy, dokopu a spojkoviště.
 */

import { readBranchVykopSegmentIdsFromMetadata } from '../constants/object-workflow/budova-milestone-engine';
import { resolveAttachmentPhaseLabel } from '../constants/object-workflow/attachment-phase';

export const WORKFLOW_OBJECT_NOTES_METADATA_KEY = 'workflowObjectNotes';

export type BudovaPripojeniNoteSource = 'BUDOVA' | 'DOKOP' | 'SPOJKOVISTE';

export const BUDOVA_PRIPOJENI_NOTE_SOURCE_LABELS: Record<BudovaPripojeniNoteSource, string> = {
  BUDOVA: 'Budova',
  DOKOP: 'Dokop',
  SPOJKOVISTE: 'Spojkoviště',
};

export interface WorkflowObjectNoteParsed {
  id: string;
  text: string;
  phaseCode: string;
  phaseLabel?: string;
  createdAt: string;
  authorEmail: string;
  /** Změny oproti PD — chybí = open. */
  status?: string;
  /** Volitelné — pokud klient uloží čas poslední úpravy textu. */
  updatedAt?: string;
}

/**
 * Poznámky založené při GeoJSON importu / sync INFO z CAD Text
 * (`id: import-…` / `import-sync-…`) — popisky z PD, ne uživatelské zápisy.
 */
export function isImportSeededWorkflowObjectNote(
  note: Pick<WorkflowObjectNoteParsed, 'id'> | { id?: string | null } | null | undefined,
): boolean {
  const id = String(note?.id ?? '').trim().toLowerCase();
  return id.startsWith('import-') || id.startsWith('import-sync-');
}

export interface BudovaPripojeniAggregatedNote {
  source: BudovaPripojeniNoteSource;
  sourceLabel: string;
  text: string;
  phaseLabel: string;
  authorEmail: string;
  createdAt: string;
}

export interface BudovaPripojeniNotesLookupEntry {
  metadata?: Record<string, unknown> | null;
  type?: string | null;
}

export type BudovaPripojeniNotesLookup = ReadonlyMap<string, BudovaPripojeniNotesLookupEntry>;

export interface BudovaPripojeniClusterInput {
  id: string;
  metadata?: Record<string, unknown> | null;
  type?: string | null;
  budovaJunctionSpojkovisteId?: string | null;
}

function readCustomFields(meta: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const m = meta ?? {};
  const cf = m['customFields'] ?? m['custom_fields'];
  if (cf && typeof cf === 'object' && !Array.isArray(cf)) return cf as Record<string, unknown>;
  return {};
}

function parseWorkflowNoteItem(item: unknown): WorkflowObjectNoteParsed | null {
  if (!item || typeof item !== 'object') return null;
  const o = item as Record<string, unknown>;
  const id = typeof o['id'] === 'string' ? o['id'].trim() : '';
  const text = typeof o['text'] === 'string' ? o['text'].trim() : '';
  if (!id || !text) return null;
  return {
    id,
    text: text.slice(0, 500),
    phaseCode: typeof o['phaseCode'] === 'string' ? o['phaseCode'] : '',
    phaseLabel: typeof o['phaseLabel'] === 'string' ? o['phaseLabel'] : undefined,
    createdAt: typeof o['createdAt'] === 'string' ? o['createdAt'] : new Date().toISOString(),
    authorEmail: typeof o['authorEmail'] === 'string' ? o['authorEmail'].trim() : '',
    status: typeof o['status'] === 'string' ? o['status'].trim() : undefined,
    updatedAt: typeof o['updatedAt'] === 'string' ? o['updatedAt'] : undefined,
  };
}

/** Parse `metadata.customFields.workflowObjectNotes` (or top-level customFields). */
export function parseWorkflowObjectNotesFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
  objectType?: string | null,
): WorkflowObjectNoteParsed[] {
  const raw = readCustomFields(metadata)[WORKFLOW_OBJECT_NOTES_METADATA_KEY];
  if (!Array.isArray(raw)) return [];
  const out: WorkflowObjectNoteParsed[] = [];
  for (const item of raw) {
    const entry = parseWorkflowNoteItem(item);
    if (!entry) continue;
    out.push({
      ...entry,
      phaseLabel:
        entry.phaseLabel?.trim()
        || (entry.phaseCode ? resolveAttachmentPhaseLabel(entry.phaseCode, objectType) : '')
        || undefined,
    });
  }
  return out;
}

function resolvePhaseLabelForNote(
  note: WorkflowObjectNoteParsed,
  objectType?: string | null,
): string {
  const stored = (note.phaseLabel ?? '').trim();
  if (stored) return stored;
  const fromCode = note.phaseCode.trim()
    ? resolveAttachmentPhaseLabel(note.phaseCode, objectType)
    : '';
  return fromCode.trim();
}

function toAggregatedNote(
  source: BudovaPripojeniNoteSource,
  note: WorkflowObjectNoteParsed,
  objectType?: string | null,
): BudovaPripojeniAggregatedNote {
  return {
    source,
    sourceLabel: BUDOVA_PRIPOJENI_NOTE_SOURCE_LABELS[source],
    text: note.text,
    phaseLabel: resolvePhaseLabelForNote(note, objectType),
    authorEmail: (note.authorEmail ?? '').trim(),
    createdAt: note.createdAt,
  };
}

function noteSortKey(note: BudovaPripojeniAggregatedNote): number {
  const t = Date.parse(note.createdAt);
  return Number.isFinite(t) ? t : 0;
}

/** Resolve spojkoviště + dokop (branch VYKOP) ids linked to a BUDOVA row. */
export function resolveBudovaPripojeniClusterObjectIds(
  budova: BudovaPripojeniClusterInput,
): { vykopSegmentIds: string[]; spojkovisteId: string | null } {
  const meta = budova.metadata ?? {};
  const custom = readCustomFields(meta);
  const spojkovisteId =
    (budova.budovaJunctionSpojkovisteId ?? '').trim()
    || (typeof meta['budovaJunctionSpojkovisteId'] === 'string'
      ? meta['budovaJunctionSpojkovisteId'].trim()
      : '')
    || (typeof custom['budovaJunctionSpojkovisteId'] === 'string'
      ? custom['budovaJunctionSpojkovisteId'].trim()
      : '')
    || null;
  return {
    vykopSegmentIds: readBranchVykopSegmentIdsFromMetadata(meta),
    spojkovisteId,
  };
}

export function buildBudovaPripojeniNotesLookup(
  objects: readonly ({ id: string } & BudovaPripojeniNotesLookupEntry)[],
): BudovaPripojeniNotesLookup {
  const map = new Map<string, BudovaPripojeniNotesLookupEntry>();
  for (const obj of objects) {
    const id = String(obj.id ?? '').trim();
    if (!id) continue;
    map.set(id, obj);
  }
  return map;
}

/** Aggregate notes from budova + linked dokop segments + spojkoviště (sorted by time). */
export function aggregateBudovaPripojeniNotes(
  budova: BudovaPripojeniClusterInput,
  lookup: BudovaPripojeniNotesLookup = new Map(),
): BudovaPripojeniAggregatedNote[] {
  const out: BudovaPripojeniAggregatedNote[] = [];
  const budovaType = budova.type ?? 'BUDOVA';

  for (const note of parseWorkflowObjectNotesFromMetadata(budova.metadata, budovaType)) {
    out.push(toAggregatedNote('BUDOVA', note, budovaType));
  }

  const { vykopSegmentIds, spojkovisteId } = resolveBudovaPripojeniClusterObjectIds(budova);

  for (const segmentId of vykopSegmentIds) {
    const linked = lookup.get(segmentId);
    if (!linked) continue;
    const type = linked.type ?? 'VYKOP';
    for (const note of parseWorkflowObjectNotesFromMetadata(linked.metadata, type)) {
      out.push(toAggregatedNote('DOKOP', note, type));
    }
  }

  if (spojkovisteId) {
    const linked = lookup.get(spojkovisteId);
    if (linked) {
      const type = linked.type ?? 'SPOJKOVISTE';
      for (const note of parseWorkflowObjectNotesFromMetadata(linked.metadata, type)) {
        out.push(toAggregatedNote('SPOJKOVISTE', note, type));
      }
    }
  }

  return out.sort((a, b) => noteSortKey(a) - noteSortKey(b));
}

/** One line: `Budova — Fáze · autor: text` */
export function formatBudovaPripojeniAggregatedNoteLine(note: BudovaPripojeniAggregatedNote): string {
  const metaParts = [note.phaseLabel, note.authorEmail].filter((p) => p.length > 0);
  const prefix = metaParts.length > 0
    ? `${note.sourceLabel} — ${metaParts.join(' · ')}`
    : note.sourceLabel;
  return `${prefix}: ${note.text}`;
}

/** Multi-line display for WF table / Excel export. */
export function formatBudovaPripojeniNotesDisplay(
  notes: readonly BudovaPripojeniAggregatedNote[],
): string {
  if (!notes.length) return '';
  return notes.map(formatBudovaPripojeniAggregatedNoteLine).join('\n');
}
